import { unique } from 'src/libs/helper'
import {
  AddUserRequest,
  UserResponse,
  InternalUserEntity,
  UserDetailResponse,
  UpdateWorksRoleRequest,
  ExternalUsersResponse,
  ExternalUserEntity,
  CreateExternalUserFormEntity,
  AddExternalUserRequest,
  ExternalUserResponse,
  ExternalUserDetailEntity,
  InternalUserDetailEntity,
  CreateInternalUserFormEntity,
  WorksRoleType,
  UserAssignProjectFormEntity,
  AssignUserProjectsRequest,
  AssignUsersProjectsRequest,
} from 'src/types'
import { CompassRoleEntity } from 'src/types/entities/CompassRole'

type ToUpdateWorksRoleRequestInput = {
  userId: number
  roleId: number
}

type ToAssignUserProjectsRequestInput = {
  formEntity: UserAssignProjectFormEntity
  isExternal: boolean
  compassRoles: CompassRoleEntity[]
}

type ToAssignUsersProjectsRequestInput = {
  users: { id: number; isExternal: boolean }[]
  projectIds: string[]
  isSendAssignEmail: boolean
  compassRoleId: number
}

export const UserTranslator = {
  toCreateUserRequests: (formEntity: CreateInternalUserFormEntity): AddUserRequest[] => {
    return formEntity.emails.map(email => ({
      email,
      roleId: formEntity.roleId,
      sendEmail: formEntity.isSendEmail,
      companyRoleIDs: unique(formEntity.customRoleIds),
    }))
  },

  toUserEntities: (responses: UserResponse[]): InternalUserEntity[] => {
    return responses.map(response => UserTranslator.toUserEntity(response))
  },

  toCreateExternalUserRequests: (
    formEntity: CreateExternalUserFormEntity
  ): AddExternalUserRequest[] => {
    return formEntity.emails.map(email => ({ email, sendEmail: formEntity.isSendEmail }))
  },

  toExternalUserEntities: (responses: ExternalUsersResponse): ExternalUserEntity[] => {
    // TODO [2023/04/15]: roleをmasterDataから動的に取得する
    const externalUserRole: WorksRoleType = {
      id: 3,
      name: 'external',
      displayName: '外部パートナー',
    }

    return (
      responses.users?.map(user => {
        return {
          ...user,
          name: user.name ?? null,
          role: externalUserRole,
          isExternal: true,
          createdAt: new Date(user.createdAt),
        }
      }) ?? []
    )
  },

  toUserEntity: (response: UserResponse): InternalUserEntity => {
    return {
      ...response,
      role: response.role,
      isExternal: false,
      customRoles: response.companyRoles.map(role => ({
        id: role.id,
        name: role.name,
        policyIds: role.policyIds,
      })),
      createdAt: new Date(response.createdAt),
    }
  },

  toUserDetailEntity: (response: UserDetailResponse): InternalUserDetailEntity => {
    const { companyRoles, ...otherResponse } = response
    return {
      ...otherResponse,
      projects:
        response.projects?.map(project => ({
          ...project,
          status: {
            ...project.status,
            priority: project.status.priority ?? undefined,
          },
          name: project.title,
          compassRole: project.role,
          isDeleting: project.isDeleting,
        })) ?? [],
      customRoles: companyRoles,
      isExternal: false,
      projectCount: response.projects?.length ?? 0,
      role: response.role,
      createdAt: new Date(response.createdAt),
    }
  },

  toExternalUserDetailEntity: (response: ExternalUserResponse): ExternalUserDetailEntity => {
    const { user } = response
    // TODO [2023/04/15]: roleをmasterDataから動的に取得する
    const externalUserRole: WorksRoleType = {
      id: 3,
      name: 'external',
      displayName: '外部パートナー',
    }

    return {
      ...user,
      projects:
        user.projects?.map(project => ({
          ...project,
          status: {
            ...project.status,
            priority: project.status.priority ?? undefined,
          },
          name: project.title,
          compassRole: project.role,
          isDeleting: project.isDeleting,
          isWorksManaged: project.isWorksManaged,
        })) ?? [],
      isExternal: true,
      projectCount: user.projects?.length ?? 0,
      role: externalUserRole,
      createdAt: new Date(user.createdAt),
    }
  },

  toAssignUserProjectsRequest: ({
    formEntity,
    isExternal,
    compassRoles,
  }: ToAssignUserProjectsRequestInput): AssignUserProjectsRequest => {
    const projects = formEntity.projects
      .map(value => {
        const compassRoleId = compassRoles.find(role => role.id === value.compassRoleId)?.id
        return { id: value.projectId, roleId: compassRoleId }
      })
      .filter((value): value is { id: string; roleId: number } => value.roleId !== undefined)

    return {
      projects,
      isExternal,
      sendAssignEmail: formEntity.isSendAssignEmail,
    }
  },

  toAssignUsersProjectsRequest: (
    input: ToAssignUsersProjectsRequestInput
  ): AssignUsersProjectsRequest => {
    const { projectIds, users, isSendAssignEmail, compassRoleId } = input

    return {
      projectIds,
      users: users.map(user => ({
        id: user.id,
        isExternal: user.isExternal,
      })),
      roleId: compassRoleId,
      sendAssignEmail: isSendAssignEmail,
    }
  },

  toUpdateWorksRoleRequest: ({
    userId,
    roleId,
  }: ToUpdateWorksRoleRequestInput): UpdateWorksRoleRequest => {
    return { userId, roleId }
  },
}
